import {
    BANKING_GENERAL_DISCLOSURE_TEXT,
    CashApyDisclosure,
    FDICDisclosure,
} from '@fruitful/compliance';
import { AnalyticsUserEvent } from '@fruitful/types';
import { Redirect, router } from 'expo-router';
import React, { useCallback, useRef, useState } from 'react';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    BodySmallRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    FullScreenSpinner,
    LabelRegular,
    ScreenContainer,
    Sheet,
    SuperscriptText,
    useToastController,
    YStack,
} from 'smoothie';

import { AccountsSection } from '../../../banking/components/add-card/AccountsSection';
import { BatchOpenCashBankingTerms } from '../../../banking/components/BankingTerms';
import {
    FRUITFUL_BATCH_OPEN_CASH_POLICY_DISCLOSURES,
    getBankingDisclosures,
} from '../../../banking/components/Disclosures';
import { KYCVerificationStatus } from '../../../banking/types/BankAccount';
import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { useViewEvents } from '../../../events/hooks/useViewEvents';
import { captureException, track } from '../../../logger/Logger';
import { SUPPORT_EMAIL } from '../../../settings/types/constants';
import { useCreateMoneyMapAccounts } from '../../hooks/useCreateMoneyMapAccounts';
import { useMoneyMapWorkflow } from '../../hooks/useMoneyMapWorkflow';
import { GoWorkflowStepType } from '../../types/GoWorkflow';
import { GoWorkflowHeader } from '../GoWorkflowHeader';
import { AccountsStepSuccess } from './accounts/AccountsStepSuccess';
import { AccountStepBlocked, KycFlowSheet } from './accounts/AccountStepKyc';

export const CashAccounts = () => {
    const { trackViewOnce } = useViewEvents();

    const toast = useToastController();
    const user = useAuthedUser();
    const [acceptTerms, setAcceptTerms] = useState<boolean>(false);
    const [isCreatingAccounts, setIsCreatingAccounts] =
        useState<boolean>(false);
    const [showKycFlowScreen, setShowKycFlowScreen] = useState<boolean>(false);
    const [showSuccessScreen, setShowSuccessScreen] = useState<boolean>(false);
    const showKycPendingScreen =
        user.syncteraKycStatus != null &&
        user.syncteraKycStatus !== KYCVerificationStatus.ACCEPTED;

    const {
        isLoadingMoneyMap,
        moneyMap,
        hasMoneyMapError,
        isLoadingCompleteness,
        refetchMoneyMapCompleteness,
        moneyMapCompleteness,
        proceedForward,
        trackWorkflowCompletedEvent,
    } = useMoneyMapWorkflow();
    const { cashAccountsStep, cardAccountsStep } =
        // use ref because we don't want this and things derived from it to change after step is submitted
        useRef(moneyMapCompleteness).current;
    const {
        isIncluded,
        isEnabled,
        memberSoloAccountsToOpen: soloAccountsToOpen,
        memberJointAccountsToAccept: jointAccountsToAccept,
        jointAccountsToOpen,
        partnerJointAccountsToAccept,
        partnerSoloAccountsToOpen,
    } = cashAccountsStep;

    const memberHasAccountsToOpen =
        soloAccountsToOpen.length > 0 || jointAccountsToOpen.length > 0;
    const memberHasAccountsToAccept = jointAccountsToAccept.length > 0;
    const isAccountInvitationsOnly =
        memberHasAccountsToAccept && !memberHasAccountsToOpen;
    const isSingleAccountAction =
        soloAccountsToOpen.length +
            jointAccountsToOpen.length +
            jointAccountsToAccept.length ===
        1;
    const isNextStepCardAccounts =
        cardAccountsStep.isIncluded && !cardAccountsStep.isCompleteForMember;

    const { mutate: createAccounts } = useCreateMoneyMapAccounts();

    const submitCashAccountsStep = useCallback(() => {
        track(AnalyticsUserEvent.MONEY_MAP_WORKFLOW_STEP_SUBMIT_CASH_ACCOUNTS);

        if (isNextStepCardAccounts) {
            proceedForward(GoWorkflowStepType.CASH_ACCOUNTS);
        } else {
            setIsCreatingAccounts(true);

            // TODO move most of this to be within the useCreateMoneyMapAccounts hook to be shared across
            // this step and the card step.
            createAccounts(
                {
                    urlParams: {
                        id: moneyMap?.id ?? '',
                    },
                    body: {
                        disclosures: {
                            disclosures: getBankingDisclosures({
                                withKyc: false,
                                enableAutoPay: false,
                                policyTypes:
                                    FRUITFUL_BATCH_OPEN_CASH_POLICY_DISCLOSURES,
                            }),
                        },
                        enableAutoPay: false,
                        submittedCardAccounts: {
                            memberSoloAccountsToOpen:
                                cardAccountsStep.memberSoloAccountsToOpen.map(
                                    (a) => a.id,
                                ),
                            jointAccountsToOpen:
                                cardAccountsStep.jointAccountsToOpen.map(
                                    (a) => a.id,
                                ),
                            memberJointAccountsToAccept:
                                cardAccountsStep.memberJointAccountsToAccept.map(
                                    (a) => a.id,
                                ),
                            partnerJointAccountsToAccept:
                                cardAccountsStep.partnerJointAccountsToAccept.map(
                                    (a) => a.id,
                                ),
                            partnerSoloAccountsToOpen:
                                cardAccountsStep.partnerSoloAccountsToOpen.map(
                                    (a) => a.id,
                                ),
                        },
                        submittedCashAccounts: {
                            memberSoloAccountsToOpen: soloAccountsToOpen.map(
                                (a) => a.id,
                            ),
                            jointAccountsToOpen: jointAccountsToOpen.map(
                                (a) => a.id,
                            ),
                            memberJointAccountsToAccept:
                                jointAccountsToAccept.map((a) => a.id),
                            partnerJointAccountsToAccept:
                                partnerJointAccountsToAccept.map((a) => a.id),
                            partnerSoloAccountsToOpen:
                                partnerSoloAccountsToOpen.map((a) => a.id),
                        },
                    },
                },
                {
                    onSettled: async (result) => {
                        if (result?.success === false) {
                            toast.show(
                                `There was an error while creating your Money Map accounts. Please reach out to support at ${SUPPORT_EMAIL}`,
                            );
                            router.replace('/roadmap/go/start');
                        } else {
                            track(
                                AnalyticsUserEvent.MONEY_MAP_WORKFLOW_ACCOUNTS_COMPLETE,
                            );
                            setShowSuccessScreen(true);
                            await refetchMoneyMapCompleteness();
                            trackWorkflowCompletedEvent(
                                GoWorkflowStepType.CASH_ACCOUNTS,
                            );
                            setIsCreatingAccounts(false);
                        }
                    },
                },
            );
        }
    }, [
        isNextStepCardAccounts,
        createAccounts,
        refetchMoneyMapCompleteness,
        moneyMap,
    ]);

    const [showSheet, setShowSheet] = useState<boolean>(false);

    const onLearnMorePress = useCallback(() => {
        track(AnalyticsUserEvent.MONEY_MAP_WORKFLOW_CASH_ACCOUNTS_SHEET_OPENED);
        setShowSheet(true);
    }, [track]);

    const onSheetClose = useCallback(() => {
        track(AnalyticsUserEvent.MONEY_MAP_WORKFLOW_CASH_ACCOUNTS_SHEET_CLOSED);
        setShowSheet(false);
    }, [track]);

    if (isLoadingMoneyMap || isCreatingAccounts || isLoadingCompleteness) {
        return <FullScreenSpinner />;
    }

    if (showSuccessScreen) {
        return (
            <AccountsStepSuccess
                currentStepType={GoWorkflowStepType.CASH_ACCOUNTS}
                isAccountInvitationsOnly={isAccountInvitationsOnly}
                isSingleAccountAction={isSingleAccountAction}
            />
        );
    }

    if (showKycPendingScreen) {
        return <AccountStepBlocked />;
    }

    if (moneyMap == null || hasMoneyMapError) {
        captureException(
            new Error('Tried to load CashAccounts step with no Money Map'),
        );
        toast.show(
            `There was an error while loading your Money Map. Please reach out to support at ${SUPPORT_EMAIL}`,
        );
        return <Redirect href="/home/roadmap" />;
    }

    // Should not be possible to trigger this, but just in case.
    if (!isIncluded || !isEnabled) {
        captureException(
            new Error('Cannot load CashAccounts step: invalid state'),
            {
                isIncluded,
                isEnabled,
                memberHasAccountsToOpen,
                memberHasAccountsToAccept,
            },
        );
        toast.show('There was an issue with the Card Accounts step.');
        return <Redirect href="/roadmap/go/start" />;
    }

    trackViewOnce('Roadmap/Go/CashAccounts');

    return (
        <>
            <GoWorkflowHeader
                stepType={GoWorkflowStepType.CASH_ACCOUNTS}
                title={
                    isAccountInvitationsOnly
                        ? 'Join Fruitful Cash Accounts'
                        : 'Open Fruitful Cash Accounts'
                }
            />
            <ScreenContainer useSafeAreaInsetTop={false}>
                <YStack marginTop={12} justifyContent="space-between">
                    <AccountsSection
                        currentStepType={GoWorkflowStepType.CASH_ACCOUNTS}
                        soloAccountsToOpen={soloAccountsToOpen}
                        jointAccountsToOpen={jointAccountsToOpen}
                        jointAccountsToAccept={jointAccountsToAccept}
                        learnMoreText={
                            <SuperscriptText
                                superscript={1}
                                textBefore="Fruitful bank accounts earn up to 4.00% APY"
                                textAfter="with no minimums."
                                textComponent={BodySmallRegular}
                                textProps={{ flex: 1 }}
                            >
                                {' '}
                                <BodySmallRegular textDecorationLine="underline">
                                    Learn more
                                </BodySmallRegular>
                            </SuperscriptText>
                        }
                        onLearnMorePress={onLearnMorePress}
                    />
                    <YStack width="100%" gap={16}>
                        <BatchOpenCashBankingTerms
                            termsAccepted={acceptTerms}
                            onPress={() => {
                                setAcceptTerms((oldVal) => {
                                    return !oldVal;
                                });
                            }}
                            backgroundColor="$gray200"
                            padding={16}
                            borderRadius={16}
                        />
                        <Button
                            disabled={!acceptTerms}
                            text={
                                isNextStepCardAccounts
                                    ? 'Accept and continue'
                                    : 'Accept and submit'
                            }
                            size={ButtonSize.LARGE}
                            variant={ButtonVariant.PRIMARY}
                            onPress={() => {
                                if (user.syncteraKycStatus == null) {
                                    setShowKycFlowScreen(true);
                                } else {
                                    submitCashAccountsStep();
                                }
                            }}
                        />
                        <YStack marginBottom={24} width="100%" gap={12}>
                            <LabelRegular color="$gray900">
                                Fruitful is a financial technology company, not
                                a bank. Deposit accounts provided by Emigrant
                                Bank, Member FDIC. The Fruitful Card is issued
                                by Emigrant Bank pursuant to license by
                                Mastercard International Incorporated.
                            </LabelRegular>
                            <CashApyDisclosure superscript={1} />
                        </YStack>
                    </YStack>
                </YStack>
                <KycFlowSheet
                    isOpen={showKycFlowScreen}
                    onKycAccepted={submitCashAccountsStep}
                    onClose={() => {
                        setShowKycFlowScreen(false);
                    }}
                />
            </ScreenContainer>
            <Sheet
                isOpen={showSheet}
                onClose={onSheetClose}
                showCloseButton={true}
                dismissOnOverlayPress={false}
                disableDrag={true}
                title="About Bank Accounts"
            >
                <YStack width="100%" gap={16}>
                    <YStack width="100%" gap={4}>
                        <BodyMediumEmphasized>
                            Bank Accounts
                        </BodyMediumEmphasized>
                        <BodyMediumRegular>
                            Bank accounts are issued by Emigrant Bank, Member
                            FDIC
                        </BodyMediumRegular>
                    </YStack>
                    <YStack width="100%" gap={4}>
                        <SuperscriptText
                            superscript={2}
                            textBefore="FDIC Insured"
                            textComponent={BodyMediumEmphasized}
                        />
                        <BodyMediumRegular>
                            Deposits held at Emigrant Bank are FDIC insured for
                            $250,000.
                        </BodyMediumRegular>
                    </YStack>
                    <YStack width="100%" gap={4}>
                        <BodyMediumEmphasized>
                            Fees & minimums
                        </BodyMediumEmphasized>
                        <BodyMediumRegular>
                            There are no account minimums or overdraft fees.
                        </BodyMediumRegular>
                    </YStack>
                    <YStack width="100%" gap={4}>
                        <SuperscriptText
                            superscript={1}
                            textBefore="Earn 4.00% APY"
                            textComponent={BodyMediumEmphasized}
                        />
                        <BodyMediumRegular>
                            You get 4.00% APY on all cash
                        </BodyMediumRegular>
                    </YStack>
                    <Button
                        marginTop={8}
                        text="Got it"
                        size={ButtonSize.LARGE}
                        variant={ButtonVariant.PRIMARY}
                        onPress={onSheetClose}
                    />
                    <LabelRegular color="$gray900">
                        {BANKING_GENERAL_DISCLOSURE_TEXT}
                    </LabelRegular>
                    <CashApyDisclosure superscript={1} />
                    <FDICDisclosure superscript={2} />
                </YStack>
            </Sheet>
        </>
    );
};
